import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { VideoCreate, VideoVisibility } from '@streamr/shared';
import { Button, Field, inputClass } from '../components/ui';
import { UploadController, type UploadProgress } from '../upload/uploadController';
import { useVideoWithStatusPolling } from '../api/hooks/useVideos';
import { formatBytes } from '../lib/format';

const VISIBILITIES: Array<{ value: VideoVisibility; label: string; hint: string }> = [
  { value: 'PRIVATE', label: 'Private', hint: 'Only you can watch it.' },
  { value: 'UNLISTED', label: 'Unlisted', hint: 'Anyone with the link can watch it.' },
  { value: 'PUBLIC', label: 'Public', hint: 'Shows in the feed and in search.' },
];

const IDLE: UploadProgress = {
  phase: 'idle',
  ratio: 0,
  uploadedParts: 0,
  totalParts: 0,
  uploadId: null,
  videoId: null,
  error: null,
};

export const UploadPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [descriptionText, setDescriptionText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [visibility, setVisibility] = useState<VideoVisibility>('PRIVATE');
  const [progress, setProgress] = useState<UploadProgress>(IDLE);

  const controllerRef = useRef<UploadController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new UploadController({ onProgress: setProgress });
  }

  // Once the bytes are in, the video's own status is what the page reports:
  // transcoding is the long part and it happens after the upload finishes.
  const transcoding = useVideoWithStatusPolling(
    progress.phase === 'processing' && progress.videoId !== null ? progress.videoId : '',
  );

  useEffect(() => {
    if (transcoding.data?.status === 'READY' && progress.videoId !== null) {
      navigate(`/watch/${progress.videoId}`);
    }
  }, [transcoding.data?.status, progress.videoId, navigate]);

  const metadata: VideoCreate = useMemo(
    () => ({
      title: title.trim(),
      description: descriptionText.trim(),
      tags: tagsText
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter((tag) => tag !== ''),
      visibility,
    }),
    [title, descriptionText, tagsText, visibility],
  );

  const start = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (file === null || metadata.title === '') return;
      void controllerRef.current?.start(file, metadata);
    },
    [file, metadata],
  );

  const busy = progress.phase === 'initializing' || progress.phase === 'uploading' || progress.phase === 'finalizing';
  const percent = Math.round(progress.ratio * 100);

  if (progress.phase === 'processing') {
    return (
      <div className="mx-auto max-w-2xl">
        <h1 className="text-xl font-semibold">Upload complete</h1>
        <p className="mt-2 text-sm text-ink-300">
          Streamr is transcoding your video into the quality ladder. This page will open the watch page as soon as it
          is ready -- you can also close it, the job keeps running.
        </p>
        <p className="mt-4 rounded-lg bg-ink-800 p-4 text-sm text-ink-200">
          Status: <span className="font-medium">{transcoding.data?.status ?? 'PROCESSING'}</span>
        </p>
        {transcoding.data?.status === 'FAILED' && (
          <p className="mt-4 text-sm text-red-300">
            Transcoding failed. The source is stored, so retrying from{' '}
            <Link to="/studio" className="underline">
              your videos
            </Link>{' '}
            will not need another upload.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={start} className="mx-auto flex max-w-2xl flex-col gap-5">
      <h1 className="text-xl font-semibold">Upload a video</h1>

      <Field label="Video file" htmlFor="file" hint="Up to 5 GB. The upload resumes if it is interrupted.">
        <input
          id="file"
          type="file"
          accept="video/*"
          disabled={busy}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          className="w-full rounded-lg border border-dashed border-ink-600 bg-ink-800 px-3 py-6 text-sm text-ink-200 file:mr-3 file:rounded-full file:border-0 file:bg-brand-500 file:px-4 file:py-2 file:text-sm file:text-white"
        />
      </Field>

      {file !== null && (
        <p className="-mt-3 text-xs text-ink-400">
          {file.name} &middot; {formatBytes(file.size)}
        </p>
      )}

      <Field label="Title" htmlFor="title">
        <input
          id="title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={150}
          required
          disabled={busy}
          className={inputClass}
        />
      </Field>

      <Field label="Description" htmlFor="description">
        <textarea
          id="description"
          value={descriptionText}
          onChange={(event) => setDescriptionText(event.target.value)}
          maxLength={5000}
          rows={4}
          disabled={busy}
          className={inputClass}
        />
      </Field>

      <Field label="Tags" htmlFor="tags" hint="Comma separated. Used by search and by up-next.">
        <input
          id="tags"
          value={tagsText}
          onChange={(event) => setTagsText(event.target.value)}
          disabled={busy}
          className={inputClass}
        />
      </Field>

      <fieldset className="flex flex-col gap-2" disabled={busy}>
        <legend className="mb-1 text-sm font-medium text-ink-100">Visibility</legend>
        {VISIBILITIES.map((option) => (
          <label key={option.value} className="flex items-start gap-3 rounded-lg border border-ink-600 p-3">
            <input
              type="radio"
              name="visibility"
              value={option.value}
              checked={visibility === option.value}
              onChange={() => setVisibility(option.value)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm text-ink-100">{option.label}</span>
              <span className="block text-xs text-ink-400">{option.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {(busy || progress.phase === 'paused') && (
        <div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-700">
            <div
              className="h-full rounded-full bg-brand-500 transition-[width]"
              style={{ width: `${percent}%` }}
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <p className="mt-2 text-xs text-ink-300">
            {progress.phase === 'paused' ? 'Paused' : 'Uploading'} &middot; {percent}% &middot;{' '}
            {progress.uploadedParts}/{progress.totalParts} parts
          </p>
        </div>
      )}

      {progress.error !== null && <p className="text-sm text-red-300">{progress.error}</p>}

      <div className="flex gap-2">
        <Button type="submit" disabled={busy || file === null || metadata.title === ''}>
          {progress.phase === 'paused' ? 'Resume upload' : busy ? 'Uploading...' : 'Upload'}
        </Button>
        {busy && (
          <Button type="button" variant="secondary" onClick={() => controllerRef.current?.pause()}>
            Pause
          </Button>
        )}
      </div>
    </form>
  );
};
